// ============================================================================
// Mutations TanStack Query — ACCUEIL E1b : publier / reagir / commenter
// ============================================================================
// Hook = TanStack Query SEULEMENT (regle parc). TOUTES les mutations sont
// OPTIMISTES avec ROLLBACK sur erreur (MASTER-GEST-E1B) : snapshot des caches
// touches dans onMutate (apres cancelQueries), restauration dans onError,
// invalidation de reconciliation dans onSettled (le serveur reste l'autorite —
// id reels, compteurs projetes, myReaction). L'AUTORISATION vit au SERVEUR
// (bind-to-fetch) : ici on ne fait qu'anticiper le verdict attendu ; un refus
// serveur (403/404) declenche le rollback + un code court mappe i18n par
// l'appelant (accueilErrorCode — jamais de code brut a l'ecran, GA-21).
//
// Piege UQ mig 144 anticipe : UNE reaction par user par publication (tous
// types confondus) — poser un autre type REMPLACE la reaction courante
// (l'optimiste decremente l'ancien compteur, incremente le nouveau).

import { useMutation, useQueryClient, type InfiniteData, type QueryClient } from '@tanstack/react-query';
import { api, ApiForbiddenError, ApiServerError } from '@gs/api-client';
import {
  ACCUEIL_FEED_KEY, accueilCommentsKey, accueilErrorCode,
  type Publication, type PublicationsPage, type PublicationComment,
  type PublicationCommentsResponse, type PublicationCategory, type ReactionType,
} from './useAccueil';

type FeedData = InfiniteData<PublicationsPage>;
type FeedSnapshot = Array<[readonly unknown[], FeedData | undefined]>;

/** Identite du viewer pour le rendu optimiste (affichage seulement — le serveur force la session). */
export interface OptimisticAuthor {
  email: string;
  name: string | null;
}

// ─── Helpers caches feed (toutes les variantes categorie en cache) ───────────

async function snapshotFeeds(qc: QueryClient): Promise<FeedSnapshot> {
  await qc.cancelQueries({ queryKey: ACCUEIL_FEED_KEY });
  return qc.getQueriesData<FeedData>({ queryKey: ACCUEIL_FEED_KEY });
}

function restoreFeeds(qc: QueryClient, snapshot: FeedSnapshot | undefined): void {
  for (const [key, data] of snapshot ?? []) qc.setQueryData(key, data);
}

function mapFeedItems(qc: QueryClient, update: (p: Publication) => Publication): void {
  qc.setQueriesData<FeedData>({ queryKey: ACCUEIL_FEED_KEY }, (data) =>
    data
      ? {
          ...data,
          pages: data.pages.map((pg) => ({ ...pg, publications: pg.publications.map(update) })),
        }
      : data,
  );
}

// ─── Publier ──────────────────────────────────────────────────────────────────

export interface PublishInput {
  title: string;
  body: string;
  category: PublicationCategory;
  visibility: 'public' | 'management';
  /** Le visuel : part AVEC le texte, en une requete multipart (fichier puis ligne au serveur). */
  image?: File | null;
  /**
   * Les pieces jointes (mig 285) : chacune part APRES la publication, par sa
   * propre requete (POST /api/publications/<id>/attachments), dans l'ordre de
   * l'editeur — jamais dans la requete de creation.
   */
  attachments?: ReadonlyArray<{ file: File; kind: 'image' | 'pdf' | 'pptx' }>;
}

const PPTX_TYPE = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';

/**
 * Le fichier tel qu'il part : son type declare est celui que le tri de
 * l'editeur a reconnu (un .pptx ou un .pdf sans type annonce par le navigateur
 * partirait en application/octet-stream, que le serveur refuse : P-UP-7).
 */
function withDeclaredType(file: File, kind: 'image' | 'pdf' | 'pptx'): File {
  const type = kind === 'pdf' ? 'application/pdf' : kind === 'pptx' ? PPTX_TYPE : file.type;
  return file.type === type ? file : new File([file], file.name, { type });
}

/**
 * Code court d'un refus de publication pour le bandeau de l'accueil (GA-21 :
 * jamais le message brut). Les refus du televersement (type, taille) ont leur
 * code ; le reste suit accueilErrorCode.
 */
export function publishErrorCode(err: unknown): string {
  const body =
    err instanceof ApiServerError || err instanceof ApiForbiddenError ? err.originalBody : undefined;
  const code = (body as { code?: unknown } | undefined)?.code;
  if (code === 'GEST_UPLOAD_TOO_LARGE') return 'too_large';
  if (code === 'GEST_UPLOAD_TYPE_REFUSED' || code === 'GEST_UPLOAD_TYPE_MISMATCH') return 'type_refused';
  return accueilErrorCode(err);
}

/** Code court quand la publication est partie mais pas toutes ses pieces jointes. */
export const ATTACHMENTS_PARTIAL = 'attachments_partial';

/**
 * Publie une annonce — prepend optimiste (id temporaire) dans chaque variante
 * de fil en cache dont le filtre couvre la categorie ; rollback sur erreur ;
 * reconciliation serveur (vrai id, posted_at serveur) a l'issue. Avec visuel :
 * le meme POST en multipart (uploadGestFile — apiFetch serialise en JSON).
 * Avec pieces jointes : chacune suit la creation, par sa requete ; une piece
 * refusee laisse la publication en place et remonte ATTACHMENTS_PARTIAL.
 * `onRefused` recoit le code court d'un refus AU NIVEAU DE LA MUTATION :
 * l'editeur se ferme des l'envoi, et un rappel passe a mutate() ne serait plus
 * appele apres son demontage (TanStack Query v5) — le bandeau resterait muet.
 */
export function usePublishPublication(author: OptimisticAuthor, onRefused?: (code: string) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ image, attachments = [], ...fields }: PublishInput) => {
      const { uploadGestFile } = await import('../lib/blobs/uploadFile');
      const created = image
        ? await uploadGestFile<{ id: string }>('/api/publications', image, { ...fields })
        : await api.post<{ id: string }>('/api/publications', fields);
      // La publication existe : chaque piece part ensuite, une a la fois (le
      // serveur relit le plafond a chaque envoi). Un refus n'arrete pas les
      // suivantes et ne retire pas la publication : il se compte et se dit.
      let failed = 0;
      for (const a of attachments) {
        try {
          await uploadGestFile(`/api/publications/${created.id}/attachments`, withDeclaredType(a.file, a.kind));
        } catch {
          failed += 1;
        }
      }
      return { id: created.id, failedAttachments: failed };
    },
    onMutate: async (input) => {
      const snapshot = await snapshotFeeds(qc);
      const temp: Publication = {
        id: `optimistic-${Date.now()}`,
        title: input.title,
        body: input.body,
        category: input.category,
        authorEmail: author.email,
        authorName: author.name,
        postedAt: new Date().toISOString(),
        // Une entree optimiste ne resout jamais d'image (le fil attend l'id
        // reel) : le visuel parait a la reconciliation, par le proxy.
        hasImage: false,
        reactions: { like: 0, love: 0, celebrate: 0 },
        viewCount: 0,
        commentCount: 0,
        myReaction: null,
        // Les pieces jointes paraissent a la reconciliation, avec leur id reel.
        attachments: [],
      };
      // Cle = [...FEED_KEY, { category }] — ne prepend que dans les variantes
      // dont le filtre couvre la publication (le snapshot vient d'etre pris).
      for (const [key, data] of snapshot) {
        if (!data || data.pages.length === 0) continue;
        const filter = (key[key.length - 1] as { category: PublicationCategory | null } | undefined)
          ?.category;
        if (filter && filter !== input.category) continue;
        const [first, ...rest] = data.pages;
        qc.setQueryData<FeedData>(key, {
          ...data,
          pages: [{ ...first, publications: [temp, ...first.publications] }, ...rest],
        });
      }
      return { snapshot };
    },
    onSuccess: (result) => {
      if (result.failedAttachments > 0) onRefused?.(ATTACHMENTS_PARTIAL);
    },
    onError: (err, _input, ctx) => {
      restoreFeeds(qc, ctx?.snapshot);
      onRefused?.(publishErrorCode(err));
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ACCUEIL_FEED_KEY }),
  });
}

// ─── Reagir (toggle — une reaction par user par publication) ─────────────────

export interface ToggleReactionInput {
  publicationId: string;
  type: ReactionType;
  /** Reaction ACTIVE courante du viewer (myReaction du fil — verdict serveur). */
  current: ReactionType | null;
}

/**
 * Toggle de reaction : meme type => retrait (DELETE) ; autre type => pose/
 * remplacement (PUT — l'optimiste bascule les compteurs ancien/nouveau).
 */
export function useToggleReaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ publicationId, type, current }: ToggleReactionInput) =>
      current === type
        ? api.delete<{ ok: boolean }>(`/api/publications/${publicationId}/reaction`)
        : api.put<{ ok: boolean }>(`/api/publications/${publicationId}/reaction`, { type }),
    onMutate: async ({ publicationId, type, current }) => {
      const snapshot = await snapshotFeeds(qc);
      mapFeedItems(qc, (p) => {
        if (p.id !== publicationId) return p;
        const reactions = { ...p.reactions };
        if (current) reactions[current] = Math.max(0, reactions[current] - 1);
        const removing = current === type;
        if (!removing) reactions[type] += 1;
        return { ...p, reactions, myReaction: removing ? null : type };
      });
      return { snapshot };
    },
    onError: (_err, _input, ctx) => restoreFeeds(qc, ctx?.snapshot),
    onSettled: () => void qc.invalidateQueries({ queryKey: ACCUEIL_FEED_KEY }),
  });
}

// ─── Commenter ────────────────────────────────────────────────────────────────

/**
 * Ajoute un commentaire — append optimiste dans le fil de discussion +
 * commentCount du fil ; le serveur retourne le commentaire reel (remplace
 * l'entree temporaire au success, rollback complet sur erreur).
 */
export function useAddComment(publicationId: string, author: OptimisticAuthor) {
  const qc = useQueryClient();
  const commentsKey = accueilCommentsKey(publicationId);
  return useMutation({
    mutationFn: (body: string) =>
      api.post<{ comment: PublicationComment }>(`/api/publications/${publicationId}/comments`, { body }),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: commentsKey });
      const feedSnapshot = await snapshotFeeds(qc);
      const commentsSnapshot = qc.getQueryData<PublicationCommentsResponse>(commentsKey);
      const tempId = `optimistic-${Date.now()}`;
      qc.setQueryData<PublicationCommentsResponse>(commentsKey, (data) => ({
        comments: [
          ...(data?.comments ?? []),
          {
            id: tempId,
            authorEmail: author.email,
            authorName: author.name,
            body,
            createdAt: new Date().toISOString(),
            canDelete: true,
          },
        ],
      }));
      mapFeedItems(qc, (p) => (p.id === publicationId ? { ...p, commentCount: p.commentCount + 1 } : p));
      return { feedSnapshot, commentsSnapshot, tempId };
    },
    onSuccess: (res, _body, ctx) => {
      // Reconciliation ciblee : l'entree temporaire prend l'identite serveur.
      qc.setQueryData<PublicationCommentsResponse>(commentsKey, (data) =>
        data
          ? { comments: data.comments.map((c) => (c.id === ctx?.tempId ? res.comment : c)) }
          : data,
      );
    },
    onError: (_err, _body, ctx) => {
      qc.setQueryData(commentsKey, ctx?.commentsSnapshot);
      restoreFeeds(qc, ctx?.feedSnapshot);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: commentsKey });
      void qc.invalidateQueries({ queryKey: ACCUEIL_FEED_KEY });
    },
  });
}

/** Supprime un commentaire (affordance canDelete — verdict serveur) : retrait optimiste + rollback. */
export function useDeleteComment(publicationId: string) {
  const qc = useQueryClient();
  const commentsKey = accueilCommentsKey(publicationId);
  return useMutation({
    mutationFn: (commentId: string) =>
      api.delete<{ ok: boolean }>(`/api/publications/${publicationId}/comments/${commentId}`),
    onMutate: async (commentId) => {
      await qc.cancelQueries({ queryKey: commentsKey });
      const feedSnapshot = await snapshotFeeds(qc);
      const commentsSnapshot = qc.getQueryData<PublicationCommentsResponse>(commentsKey);
      qc.setQueryData<PublicationCommentsResponse>(commentsKey, (data) =>
        data ? { comments: data.comments.filter((c) => c.id !== commentId) } : data,
      );
      mapFeedItems(qc, (p) =>
        p.id === publicationId ? { ...p, commentCount: Math.max(0, p.commentCount - 1) } : p,
      );
      return { feedSnapshot, commentsSnapshot };
    },
    onError: (_err, _commentId, ctx) => {
      qc.setQueryData(commentsKey, ctx?.commentsSnapshot);
      restoreFeeds(qc, ctx?.feedSnapshot);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: commentsKey });
      void qc.invalidateQueries({ queryKey: ACCUEIL_FEED_KEY });
    },
  });
}
